import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../lib/ThemeContext';
import { Sun, Moon, Menu, X, ChevronDown, Shield, Server, Headphones, BrainCircuit } from 'lucide-react';

const navLinks = [
  { name: 'Solutions', path: '/solutions', hasMega: true },
  { name: 'Industries', path: '/industries' },
  { name: 'Managed Services', path: '/managed-services' },
  { name: 'Partners', path: '/partners' },
  { name: 'Company', path: '/company' },
  { name: 'Careers', path: '/careers' },
];

const megaMenuItems = [
  { title: 'AI & Cloud Platform', icon: BrainCircuit, link: '/solutions', items: [
    { name: 'AI Infrastructure', desc: 'GPU-ready clusters, HPC storage', link: '/solutions' },
    { name: 'Kubernetes Platform', desc: 'Nutanix NKP, RedHat OpenShift', link: '/devops' },
    { name: 'MLOps', desc: 'Data pipelines & model serving', link: '/solutions' },
    { name: 'Edge AI', desc: 'Smart city & branch intelligence', link: '/solutions' },
  ]},
  { title: 'Cybersecurity', icon: Shield, link: '/solutions', items: [
    { name: 'SIEM / SOC Operations', desc: 'LogRhythm-powered threat detection', link: '/solutions' },
    { name: 'Network Defense', desc: 'Firewall, IDS/IPS, Zero Trust', link: '/solutions' },
    { name: 'ISO Compliance', desc: '27001, 9001 audit support', link: '/solutions' },
    { name: 'Identity & Access', desc: 'IAM, MFA, PAM solutions', link: '/solutions' },
  ]},
  { title: 'Infrastructure', icon: Server, link: '/solutions', items: [
    { name: 'Private Cloud / HCI', desc: 'Nutanix Private Cloud / HCI', link: '/solutions' },
    { name: 'Server & Storage', desc: 'Enterprise compute solutions', link: '/solutions' },
    { name: 'Network Infrastructure', desc: 'SD-WAN, routing, switching', link: '/solutions' },
    { name: 'Document Management', desc: 'Digital workflow systems', link: '/solutions' },
  ]},
  { title: 'Managed Services', icon: Headphones, link: '/managed-services', items: [
    { name: 'AI & Cloud Ops', desc: 'K8s & GPU workload management', link: '/managed-services' },
    { name: '24/7 Monitoring', desc: 'Proactive NOC operations', link: '/managed-services' },
    { name: 'IT Consulting', desc: 'Strategy & architecture', link: '/managed-services' },
    { name: 'Field Services', desc: 'On-site support nationwide', link: '/managed-services' },
  ]},
];

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();
  const megaRef = useRef(null);
  const megaTimeout = useRef(null);

  const [bannerVisible, setBannerVisible] = useState(true);

  useEffect(() => { setMobileOpen(false); setMegaOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => { if (megaRef.current && !megaRef.current.contains(e.target)) setMegaOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      setBannerVisible(y < 50);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const megaEnter = () => { clearTimeout(megaTimeout.current); setMegaOpen(true); };
  const megaLeave = () => { megaTimeout.current = setTimeout(() => setMegaOpen(false), 150); };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* Everest Cloud Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] text-center py-1.5 transition-transform duration-300" style={{
        background: theme === 'dark' ? 'rgba(0,113,227,0.12)' : 'rgba(0,113,227,0.06)',
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(0,113,227,0.15)' : 'rgba(0,113,227,0.1)'}`,
        transform: bannerVisible ? 'translateY(0)' : 'translateY(-100%)',
      }}>
        <a href="https://everestcloud.com.np" target="_blank" rel="noopener noreferrer" className="text-[12px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5">
          Introducing <span className="font-semibold text-[#0071E3]">Everest Cloud</span> — Nepal's Cloud Platform by DNS <span className="text-[11px]">→</span>
        </a>
      </div>
      <nav data-testid="navbar" className="fixed left-0 right-0 z-50 transition-all duration-300" style={{
        top: bannerVisible ? '33px' : '0px',
        background: theme === 'dark' ? 'rgba(10,10,10,0.8)' : 'rgba(255,255,255,0.8)',
        backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
      }}>
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center" data-testid="navbar-logo">
              <img src={theme === 'dark' ? '/images/dns-logo-white.png' : '/images/dns-logo.png'} alt="DNS" className="h-12 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((link) => (
                <div key={link.name} ref={link.hasMega ? megaRef : undefined}
                  onMouseEnter={link.hasMega ? megaEnter : undefined} onMouseLeave={link.hasMega ? megaLeave : undefined} className="relative">
                  <Link to={link.path} data-testid={`nav-link-${link.name.toLowerCase().replace(/\\s+/g, '-')}`}
                    className={`px-3 py-1.5 text-[15px] font-medium rounded-md transition-colors flex items-center gap-1 ${isActive(link.path) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    {link.name}
                    {link.hasMega && <ChevronDown className={`w-3 h-3 transition-transform ${megaOpen ? 'rotate-180' : ''}`} />}
                  </Link>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-1.5">
              <button data-testid="theme-toggle-switch" onClick={toggleTheme} className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle theme">
                {theme === 'dark' ? <Sun className="w-[15px] h-[15px]" /> : <Moon className="w-[15px] h-[15px]" />}
              </button>
              <Link to="/contact" data-testid="navbar-primary-cta-button" className="hidden sm:inline-flex items-center px-3.5 py-1.5 text-[15px] font-medium text-white bg-[#0071E3] rounded-full hover:bg-[#0077ED] transition-colors">
                Get in Touch
              </Link>
              <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground">
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {megaOpen && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.15 }}
              onMouseEnter={megaEnter} onMouseLeave={megaLeave} data-testid="mega-menu"
              className="absolute left-0 right-0 hidden lg:block" style={{
                background: theme === 'dark' ? 'rgba(10,10,10,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              }}>
              <div className="max-w-6xl mx-auto px-5 py-5">
                <div className="grid grid-cols-4 gap-8">
                  {megaMenuItems.map((cat) => (
                    <div key={cat.title}>
                      <div className="flex items-center gap-1.5 mb-3">
                        <cat.icon className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{cat.title}</span>
                      </div>
                      <div className="space-y-0.5">
                        {cat.items.map((item) => (
                          <Link key={item.name} to={item.link} className="block px-2 py-1.5 -mx-2 rounded-lg hover:bg-muted/50 transition-colors group">
                            <div className="text-[13px] font-medium group-hover:text-foreground transition-colors">{item.name}</div>
                            <div className="text-[11px] text-muted-foreground">{item.desc}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden" data-testid="mobile-menu"
              style={{ background: theme === 'dark' ? 'rgba(10,10,10,0.98)' : 'rgba(255,255,255,0.98)' }}>
              <div className="px-5 py-4 space-y-0.5">
                {navLinks.map((link) => (
                  <Link key={link.name} to={link.path} className={`block px-3 py-2 text-[15px] font-medium rounded-lg transition-colors ${isActive(link.path) ? 'text-foreground' : 'text-muted-foreground'}`}>{link.name}</Link>
                ))}
                <Link to="/devops" className="block px-3 py-2 text-[15px] font-medium text-muted-foreground rounded-lg">DevOps & Cloud Native</Link>
                <div className="pt-2"><Link to="/contact" className="block px-4 py-2.5 text-[15px] font-medium text-center text-white bg-[#0071E3] rounded-full">Get in Touch</Link></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      <div style={{ height: bannerVisible ? '89px' : '56px', transition: 'height 0.3s ease' }} />
    </>
  );
}
